/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React from 'react';
import { Box, Divider, Typography } from '@mui/material';

import AnnualSchedule from './AnnualSchedule';
import BiAnnualSchedule from './BiAnnualSchedule';
import QuarterlySchedule from './QuarterlySchedule';
import MonthlySchedule from './MonthlySchedule';
import { DayOptions, ScheduleTypes, WeekOptions } from '../../../../../Shared/Constants/App';
import { IScheduleRendererProps } from './CreateSchedule.Model';
import { buttonStyles } from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { wrapperStyles } from '../../AllSchedules/AllJobs.Style';
import WeeklySchedule from '../../AllSchedules/ModifySchedule/Frequencies/WeeklySchedule';
import { Labels } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import { useTranslation } from 'react-i18next';

const ScheduleRenderer: React.FC<IScheduleRendererProps> = ({
  control,
  selectFrequency,
  annualConfig,
  biAnnualConfig,
  quarterlyConfig,
  monthlyConfig,
  weeklyConfig,
}) => {
  const { t } = useTranslation(['jobManagement']);

  const { headingText, divider } = buttonStyles;
  const { daysStyle, radioButton, radioButtonText, textStyle, quarterlyBoxStyle } = wrapperStyles;
  const renderSchedule = (selectedFrequency: string) => {
    switch (selectedFrequency) {
      case ScheduleTypes.Annual:
        return (
          <>
            <Box>
              <Typography sx={headingText}>{t('jobManagement:serviceDays')}</Typography>
              <AnnualSchedule t={t} control={control} {...annualConfig} />
            </Box>
            <Divider sx={divider} />
          </>
        );

      case ScheduleTypes.BiAnnual:
        return (
          <>
            <Box>
              <Typography sx={headingText}>{t('jobManagement:serviceDays')}</Typography>
              <BiAnnualSchedule t={t} control={control} {...biAnnualConfig} />
            </Box>
            <Divider sx={divider} />
          </>
        );

      case ScheduleTypes.Quarterly:
        return (
          <>
            <Box>
              <Typography sx={headingText}>{t('jobManagement:serviceDays')}</Typography>
              <QuarterlySchedule
                t={t}
                control={control}
                radioButton={radioButton}
                radioButtonText={radioButtonText}
                textStyle={textStyle}
                quarterBoxStyle={quarterlyBoxStyle}
                {...quarterlyConfig}
              />
            </Box>
            <Divider sx={divider} />
          </>
        );

      case ScheduleTypes.Monthly:
        return (
          <>
            <Box>
              <Typography sx={headingText}>{t('jobManagement:serviceDays')}</Typography>
              <MonthlySchedule
                t={t}
                radioButton={radioButton}
                radioButtonText={radioButtonText}
                textStyle={textStyle}
                quarterBoxStyle={quarterlyBoxStyle}
                WeekOptions={WeekOptions}
                DayOptions={DayOptions}
                daysStyle={daysStyle}
                Labels={Labels}
                {...monthlyConfig}
              />
            </Box>
            <Divider sx={divider} />
          </>
        );

      case ScheduleTypes.Fortnightly:
      case ScheduleTypes.Weekly:
        return (
          <>
            <Box>
              <Typography sx={headingText}>{t('jobManagement:serviceDays')}</Typography>
              <WeeklySchedule daysStyle={daysStyle} {...weeklyConfig} />
            </Box>
            <Divider sx={divider} />
          </>
        );

      default:
        return null;
    }
  };

  return renderSchedule(selectFrequency);
};

export default ScheduleRenderer;
